import React from 'react';

export type MetricColor = 'green' | 'red' | 'yellow' | 'neutral';

interface MetricCellProps {
  value: number | string | null;
  unit?: string;
  color: MetricColor;
  stale?: boolean;
  tooltip?: string;
  onClick?: () => void;
}

/**
 * MetricCell - Reusable component for color-coded metric display
 *
 * Used in Coach Dashboard "My Clients" table for:
 * - Total Loss (Column 3)
 * - Current Weight (Column 4)
 * - Daily Change (Column 5)
 * - Weekly Average (Column 6)
 * - Weekly Change (Column 7)
 *
 * Color Coding:
 * - GREEN: Success (weight loss, stable weight)
 * - RED: Warning (weight gain)
 * - YELLOW: Stale data (not from today/current week)
 * - NEUTRAL: No change or no data
 */
export const MetricCell: React.FC<MetricCellProps> = ({
  value,
  unit,
  color,
  stale = false,
  tooltip,
  onClick
}) => {
  const getColorStyles = () => {
    switch (color) {
      case 'green':
        return {
          bg: '#22c55e',      // Vibrant green background
          text: '#ffffff',    // White text for contrast
          border: '#16a34a'   // Darker green border
        };
      case 'red':
        return {
          bg: '#ef4444',      // Vibrant red background
          text: '#ffffff',    // White text for contrast
          border: '#dc2626'   // Darker red border
        };
      case 'yellow':
        return {
          bg: '#fbbf24',      // Vibrant amber/yellow
          text: '#78350f',    // Dark brown text
          border: '#f59e0b'   // Darker amber border
        };
      case 'neutral':
        return {
          bg: '#64748b',      // Slate gray
          text: '#ffffff',    // White text
          border: '#475569'   // Darker slate border
        };
      default:
        return {
          bg: '#64748b',
          text: '#ffffff',
          border: '#475569'
        };
    }
  };

  const colors = getColorStyles();
  const displayValue = value !== null && value !== undefined ? value : '--';

  return (
    <>
      <div
        className={`metric-cell ${stale ? 'stale' : ''} ${onClick ? 'clickable' : ''}`}
        style={{
          backgroundColor: colors.bg,
          color: colors.text,
          borderColor: colors.border
        }}
        onClick={onClick}
        title={tooltip}
      >
        <span className="metric-value">
          {displayValue}
          {unit && displayValue !== '--' && <span className="metric-unit">{unit}</span>}
        </span>
      </div>

      <style>{`
        .metric-cell {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 6px 12px;
          border-radius: 6px;
          border: 1px solid;
          font-size: 14px;
          font-weight: 600;
          min-width: 70px;
          transition: all 0.2s ease;
        }

        .metric-cell.clickable {
          cursor: pointer;
        }

        .metric-cell.clickable:hover {
          opacity: 0.8;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .metric-cell.stale {
          border-style: dashed;
          border-width: 2px;
        }

        .metric-value {
          display: flex;
          align-items: baseline;
          gap: 2px;
        }

        .metric-unit {
          font-size: 11px;
          font-weight: 500;
          opacity: 0.8;
          margin-left: 2px;
        }
      `}</style>
    </>
  );
};
